/**
 * 浏览器端音频截取 → WAV Blob（OfflineAudioContext）。
 */
import { fetchMediaBlob } from '@/features/studio/canvas/media-compose'

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
}

function encodeWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const bitDepth = 16
  const samples = buffer.length
  const blockAlign = (numChannels * bitDepth) / 8
  const dataSize = samples * blockAlign
  const ab = new ArrayBuffer(44 + dataSize)
  const view = new DataView(ab)

  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeString(view, 8, 'WAVE')
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitDepth, true)
  writeString(view, 36, 'data')
  view.setUint32(40, dataSize, true)

  const channels: Float32Array[] = []
  for (let c = 0; c < numChannels; c++) channels.push(buffer.getChannelData(c))

  let offset = 44
  for (let i = 0; i < samples; i++) {
    for (let c = 0; c < numChannels; c++) {
      const s = Math.max(-1, Math.min(1, channels[c][i] ?? 0))
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
      offset += 2
    }
  }
  return new Blob([ab], { type: 'audio/wav' })
}

/** 截取 [startSec, endSec)，返回 audio/wav Blob */
export async function trimAudioToWav(
  url: string,
  startSec: number,
  endSec: number,
): Promise<Blob> {
  const start = Math.max(0, startSec)
  const end = Math.max(start + 0.05, endSec)
  const blob = await fetchMediaBlob(url)
  const ab = await blob.arrayBuffer()
  const offlineProbe = new OfflineAudioContext(1, 1, 44100)
  const decoded = await offlineProbe.decodeAudioData(ab.slice(0))

  const sampleRate = decoded.sampleRate
  const startSample = Math.min(decoded.length, Math.floor(start * sampleRate))
  const endSample = Math.min(decoded.length, Math.ceil(end * sampleRate))
  const frameCount = Math.max(1, endSample - startSample)
  const channels = decoded.numberOfChannels

  const offline = new OfflineAudioContext(channels, frameCount, sampleRate)
  const sliced = offline.createBuffer(channels, frameCount, sampleRate)
  for (let c = 0; c < channels; c++) {
    const src = decoded.getChannelData(c).subarray(startSample, endSample)
    sliced.copyToChannel(src, c, 0)
  }
  const source = offline.createBufferSource()
  source.buffer = sliced
  source.connect(offline.destination)
  source.start(0)
  const rendered = await offline.startRendering()
  return encodeWav(rendered)
}
